package fr.frux.feature.treedetail.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.OpenInNew
import androidx.compose.material.icons.outlined.WarningAmber
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.Role
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.treedetail.ImportedTreeSource
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_imported_bordeaux
import fr.frux.feature.treedetail.resources.treedetail_imported_bordeaux_license
import fr.frux.feature.treedetail.resources.treedetail_imported_grenoble
import fr.frux.feature.treedetail.resources.treedetail_imported_grenoble_license
import fr.frux.feature.treedetail.resources.treedetail_imported_lozere
import fr.frux.feature.treedetail.resources.treedetail_imported_lozere_license
import fr.frux.feature.treedetail.resources.treedetail_imported_lyon
import fr.frux.feature.treedetail.resources.treedetail_imported_lyon_license
import fr.frux.feature.treedetail.resources.treedetail_imported_metz
import fr.frux.feature.treedetail.resources.treedetail_imported_metz_license
import fr.frux.feature.treedetail.resources.treedetail_imported_montpellier
import fr.frux.feature.treedetail.resources.treedetail_imported_montpellier_license
import fr.frux.feature.treedetail.resources.treedetail_imported_nantes
import fr.frux.feature.treedetail.resources.treedetail_imported_nantes_license
import fr.frux.feature.treedetail.resources.treedetail_imported_osm
import fr.frux.feature.treedetail.resources.treedetail_imported_osm_license
import fr.frux.feature.treedetail.resources.treedetail_imported_paris
import fr.frux.feature.treedetail.resources.treedetail_imported_paris_license
import fr.frux.feature.treedetail.resources.treedetail_imported_plaine_commune
import fr.frux.feature.treedetail.resources.treedetail_imported_plaine_commune_license
import fr.frux.feature.treedetail.resources.treedetail_imported_rennes
import fr.frux.feature.treedetail.resources.treedetail_imported_rennes_license
import fr.frux.feature.treedetail.resources.treedetail_imported_strasbourg
import fr.frux.feature.treedetail.resources.treedetail_imported_strasbourg_license
import fr.frux.feature.treedetail.resources.treedetail_imported_toulouse
import fr.frux.feature.treedetail.resources.treedetail_imported_toulouse_license
import fr.frux.feature.treedetail.resources.treedetail_roadside_warning_body
import fr.frux.feature.treedetail.resources.treedetail_roadside_warning_title
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * Origine d'un arbre importé en masse (#476, #501, ADR 0010) : à la place du profil du compte
 * d'import, qui n'est personne, la source et sa licence. Le tap ouvre la page du jeu de données de
 * la source, qui porte l'attribution due sur toutes les plateformes.
 */
@Composable
internal fun ImportSourceSection(
    source: ImportedTreeSource,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    val (labelRes, licenseRes) = source.stringResources()
    Column(modifier = modifier.fillMaxWidth()) {
        if (source.roadside) {
            RoadsideWarning()
            Spacer(Modifier.height(FruxSpacing.sm))
        }
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .clickable(role = Role.Button) { uriHandler.openUri(source.url) }
                    .padding(vertical = FruxSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(labelRes),
                    style = MaterialTheme.typography.bodyLarge,
                )
                Text(
                    text = stringResource(licenseRes),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            Icon(
                imageVector = Icons.AutoMirrored.Outlined.OpenInNew,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/**
 * Mise en garde d'accès portée par la source entière (#722, arbres d'alignement des routes de la
 * Lozère) : l'information vient du jeu, pas d'un passage sur place, et elle est neutre. Tonalité
 * `tertiaryContainer`, distincte du bandeau de dangerosité d'espèce (`errorContainer`, ADR 0015).
 */
@Composable
private fun RoadsideWarning(modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.tertiaryContainer,
        shape = MaterialTheme.shapes.medium,
    ) {
        Row(
            modifier = Modifier.padding(FruxSpacing.md),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Icon(
                imageVector = Icons.Outlined.WarningAmber,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onTertiaryContainer,
            )
            Column {
                Text(
                    text = stringResource(Res.string.treedetail_roadside_warning_title),
                    style = MaterialTheme.typography.titleSmall,
                    color = MaterialTheme.colorScheme.onTertiaryContainer,
                )
                Text(
                    text = stringResource(Res.string.treedetail_roadside_warning_body),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onTertiaryContainer,
                )
            }
        }
    }
}

/** Libellé et licence de chaque source importée (#501) : treize comptes système, treize libellés. */
private fun ImportedTreeSource.stringResources(): Pair<StringResource, StringResource> =
    when (this) {
        ImportedTreeSource.OSM -> {
            Res.string.treedetail_imported_osm to Res.string.treedetail_imported_osm_license
        }

        ImportedTreeSource.RENNES -> {
            Res.string.treedetail_imported_rennes to Res.string.treedetail_imported_rennes_license
        }

        ImportedTreeSource.MONTPELLIER -> {
            Res.string.treedetail_imported_montpellier to Res.string.treedetail_imported_montpellier_license
        }

        ImportedTreeSource.BORDEAUX -> {
            Res.string.treedetail_imported_bordeaux to Res.string.treedetail_imported_bordeaux_license
        }

        ImportedTreeSource.LYON -> {
            Res.string.treedetail_imported_lyon to Res.string.treedetail_imported_lyon_license
        }

        ImportedTreeSource.STRASBOURG -> {
            Res.string.treedetail_imported_strasbourg to Res.string.treedetail_imported_strasbourg_license
        }

        ImportedTreeSource.NANTES -> {
            Res.string.treedetail_imported_nantes to Res.string.treedetail_imported_nantes_license
        }

        ImportedTreeSource.TOULOUSE -> {
            Res.string.treedetail_imported_toulouse to Res.string.treedetail_imported_toulouse_license
        }

        ImportedTreeSource.PLAINE_COMMUNE -> {
            Res.string.treedetail_imported_plaine_commune to Res.string.treedetail_imported_plaine_commune_license
        }

        ImportedTreeSource.PARIS -> {
            Res.string.treedetail_imported_paris to Res.string.treedetail_imported_paris_license
        }

        ImportedTreeSource.GRENOBLE -> {
            Res.string.treedetail_imported_grenoble to Res.string.treedetail_imported_grenoble_license
        }

        ImportedTreeSource.METZ -> {
            Res.string.treedetail_imported_metz to Res.string.treedetail_imported_metz_license
        }

        ImportedTreeSource.LOZERE -> {
            Res.string.treedetail_imported_lozere to Res.string.treedetail_imported_lozere_license
        }
    }
